import { useMemo } from 'react';
import { PureQueryOptions } from '@apollo/client';

import {
  GroupCommsIndexBadgesDocument,
  useGroupCommsIndexBadgesQuery,
} from 'core/graphql';
import { filterCommsStudents } from 'core/utils/mailRecipients';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import {
  IBrowserConversation,
  useConversationsBrowser,
} from './useConversationsBrowser';

/**
 * Communicator middle-column "badge engine" (spec 0008): ONE server-side
 * `groupCommsIndex` aggregate — per-student unread/unhandled splits plus
 * whole-group totals — replaces the old client-side tally that drained up
 * to 10 cursor pages of `conversationsBrowser` (~200 rows, 10 sequential
 * round-trips) on every group select. Groups beyond the old ~200-dialog
 * drain cap now get CORRECT badges too.
 *
 * Presentation rules stay here (they're locale/UX, not data): hide a fully
 * terminated student with nothing unread, attention-first ordering. The
 * mail threads list moved to its own paginated hook (`useGroupMailThreads`).
 */

/** How often the badge queries re-poll as a safety net (covers per-message
 * read/flag mutations that don't refetch conversation aggregates). */
export const BADGE_POLL_INTERVAL_MS = 60000;

export interface ICommsStudentEntry {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  /** False for a workspace-DM counterpart that is not in the group roster. */
  inRoster: boolean;
  terminated: boolean;
  unread: number;
  /** Unread split by channel — powers the Slack | Почта toggle badges. */
  unreadSlack: number;
  unreadMail: number;
  unhandled: boolean;
  /** «Есть необработанное» split by channel — so the Slack | Почта toggle
   *  shows which side still has an inbound message nobody ticked. */
  unhandledSlack: boolean;
  unhandledMail: boolean;
}

/** Строка преподавателя (ТЗ 0032): та же форма, что у студента, минус
 *  ростерные флаги — у преподавателя нет договора и «отчисления». */
export interface ICommsTeacherEntry {
  teacherId: string;
  firstName: string;
  lastName: string;
  email: string;
  unread: number;
  unreadSlack: number;
  unreadMail: number;
  unhandled: boolean;
  unhandledSlack: boolean;
  unhandledMail: boolean;
}

export interface IGroupCommsIndex {
  /** Roster as the communicator lists it (closed-agreement students with
   * nothing unread are hidden — `filterCommsStudents` default). */
  students: ICommsStudentEntry[];
  /** The WHOLE roster in the same order, closed agreements included. Only the
   * broadcast picker uses it, behind its «включая закрытые договоры» switch
   * (задача ERP #67) — every other list keeps `students`. */
  rosterStudents: ICommsStudentEntry[];
  teachers: ICommsTeacherEntry[];
  channelsUnread: number;
  channelsUnhandled: boolean;
  /** Group-wide unread totals per channel (Slack = channels + DMs; mail = all
   * gmail threads) — for the whole-group Slack | Почта toggle badges. */
  slackUnreadTotal: number;
  mailUnreadTotal: number;
  /** Whole-group «есть необработанное» per channel (Slack = channels + DMs;
   * mail = all gmail threads) — for the whole-group Slack | Почта toggle. */
  slackUnhandled: boolean;
  mailUnhandled: boolean;
  /** Dialogs still carrying unread OR unhandled — i.e. exactly what the bulk
   * «Прочитано и обработано» button would clear (spec 0030). Use THIS for the
   * button's enabled state and its confirm count, never the totals above:
   * those are folded over visible rows only (a terminated student with zero
   * unread is dropped), so they would understate what the server clears. */
  pendingConversations: number;
  loading: boolean;
  /** Refetch descriptor for the badge aggregate — cheap; pass to a mutation's
   * `refetchQueries` when its payload can't patch the badges locally. */
  badgeRefetch: PureQueryOptions;
}

export const useGroupCommsIndex = (groupId: string): IGroupCommsIndex => {
  const {
    data,
    loading,
    startPolling,
    stopPolling,
  } = useGroupCommsIndexBadgesQuery({
    variables: { groupId },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });
  // Visibility-aware badge poll (spec 0008): only while a group is selected
  // AND the tab is visible.
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    BADGE_POLL_INTERVAL_MS,
    Boolean(groupId),
  );

  const badgeRefetch = useMemo<PureQueryOptions>(
    () => ({
      query: GroupCommsIndexBadgesDocument,
      variables: { groupId },
    }),
    [groupId],
  );

  const index = data?.groupCommsIndex;

  const rosterStudents = useMemo<ICommsStudentEntry[]>(() => {
    const rows: ICommsStudentEntry[] = (index?.students || []).map((row) => ({
      studentId: row.studentId,
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      inRoster: row.inRoster,
      terminated: row.terminated,
      unread: row.unread,
      unreadSlack: row.unreadSlack,
      unreadMail: row.unreadMail,
      unhandled: row.unhandled,
      unhandledSlack: row.unhandledSlack,
      unhandledMail: row.unhandledMail,
    }));
    return rows.sort((a, b) => {
      // Students with something to process (unread messages or a dialog
      // waiting for a reply) rise to the top — alphabetically among
      // themselves — then everyone else alphabetically.
      const aAttention = a.unhandled || a.unread > 0;
      const bAttention = b.unhandled || b.unread > 0;
      return (
        Number(bAttention) - Number(aAttention) ||
        a.lastName.localeCompare(b.lastName) ||
        a.firstName.localeCompare(b.firstName)
      );
    });
  }, [index]);

  // A terminated-only student stays visible while they still have unread
  // messages.
  const students = useMemo(() => filterCommsStudents(rosterStudents), [
    rosterStudents,
  ]);

  const teachers = useMemo<ICommsTeacherEntry[]>(
    () =>
      (index?.teachers || [])
        .map((row) => ({
          teacherId: row.teacherId,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
          unread: row.unread,
          unreadSlack: row.unreadSlack,
          unreadMail: row.unreadMail,
          unhandled: row.unhandled,
          unhandledSlack: row.unhandledSlack,
          unhandledMail: row.unhandledMail,
        }))
        // Преподавателей единицы, поэтому фильтра «скрыть пустых» нет: строка
        // нужна и без переписки — чтобы было куда написать первым.
        .sort(
          (a, b) =>
            Number(b.unhandled || b.unread > 0) -
              Number(a.unhandled || a.unread > 0) ||
            a.lastName.localeCompare(b.lastName) ||
            a.firstName.localeCompare(b.firstName),
        ),
    [index],
  );

  return {
    students,
    rosterStudents,
    teachers,
    channelsUnread: index?.channelsUnread || 0,
    channelsUnhandled: Boolean(index?.channelsUnhandled),
    slackUnreadTotal: index?.slackUnreadTotal || 0,
    mailUnreadTotal: index?.mailUnreadTotal || 0,
    slackUnhandled: Boolean(index?.slackUnhandled),
    mailUnhandled: Boolean(index?.mailUnhandled),
    pendingConversations: index?.pendingConversations || 0,
    loading,
    badgeRefetch,
  };
};

/**
 * The group's gmail threads for the «Почта» tab — the canonical paginated
 * browser feed filtered to `gmail_thread` (spec 0008; used to piggyback on
 * the badge drain). `active` defers the fetch until the mail tab is actually
 * opened — most group selections never look at mail (round 2).
 */
export const useGroupMailThreads = (
  groupId: string,
  active = true,
): {
  threads: IBrowserConversation[];
  hasNextPage: boolean;
  loadMore: () => void;
  loading: boolean;
} => {
  const {
    conversations,
    hasNextPage,
    loadMore,
    loading,
  } = useConversationsBrowser({
    groupId: groupId || undefined,
    kinds: groupId ? ['gmail_thread'] : undefined,
    unhandledOnly: false,
    skip: !groupId || !active,
  });
  return { threads: conversations, hasNextPage, loadMore, loading };
};
